import { MyBusiness } from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { CaseIcon } from "@/shared/icons";
import { TabsNavigation } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import SelectScreenCardsList from "@/shared/widgets/SelectScreenCardsList";
import SelectScreenInfo from "@/shared/widgets/SelectScreenInfo";
import SelectScreenWrapper from "@/shared/widgets/SelectScreenWrapper";
import { BusinessSelectTab } from "@/domains/business/constants/businessSelectTab";
import {
  businessSelectEmployeeSelector,
  businessSelectOwnerSelector,
  businessSelectSelectedTabSelector,
  businessSelectTabsIconsSelector,
  businessSelectTabsSelector,
  setInfo,
  setSelectSelectedTabs,
} from "@/domains/business/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";

type Props = {
  isLoading: boolean;
};

export default function BusinessSelectContainer({ isLoading }: Props) {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();

  const ownerList = useAppSelector(businessSelectOwnerSelector);
  const employeeList = useAppSelector(businessSelectEmployeeSelector);
  const tabsList = useAppSelector(businessSelectTabsSelector);
  const tabsIconsList = useAppSelector(businessSelectTabsIconsSelector);
  const activeTab = useAppSelector(businessSelectSelectedTabSelector);

  const setActiveTab = useCallback(
    (tab: BusinessSelectTab) => {
      dispatch(setSelectSelectedTabs(tab));
    },
    [dispatch],
  );

  const onChangeSelected = useCallback(
    (business: MyBusiness) => dispatch(setInfo(business)),
    [dispatch],
  );

  return (
    <SelectScreenWrapper
      title={t("business.selectScreen.title")}
      subTitle={t("business.selectScreen.description")}
      src="/svg/business/my-business.svg"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {tabsList?.length > 0 && (
            <TabsNavigation
              tabs={tabsList || []}
              selectedTab={activeTab}
              i18fileName="business"
              i18Path="business.selectScreen.tabs"
              onClick={setActiveTab}
              className="mt-4"
              Icons={tabsIconsList}
            />
          )}
          <SelectScreenInfo
            count={
              activeTab === BusinessSelectTab.MYBUSINESS ? ownerList?.length : employeeList?.length
            }
            countText={
              activeTab === BusinessSelectTab.MYBUSINESS
                ? t("business.selectScreen.ownerCount")
                : t("business.selectScreen.employeeCount")
            }
            description={t("business.selectScreen.selectInfo")}
            Icon={CaseIcon}
          />
          <SelectScreenCardsList
            list={activeTab === BusinessSelectTab.MYBUSINESS ? ownerList : employeeList}
            maxCards={2}
            getCardImage={getBusinessImage}
            onChangeSelected={onChangeSelected}
            isLogo
            Icon={CaseIcon}
            isGrid={activeTab === BusinessSelectTab.WORKPLACES}
          />
        </>
      )}
    </SelectScreenWrapper>
  );
}
